import React, {useContext, useEffect, useState} from 'react';
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {Button, Descriptions, Popconfirm, Select, Table, Tag} from "antd";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import {useGetPlayerTrophyInfo, useGetPlayerTrophyMissionInfo, useSetTrophyMissionManual, useSetTrophyFinalReward, useGetTrophyMissionGroup} from "../../usePlayerData";

const PlayerTrophy = () => {
    const {playerData} = useContext(PlayerContext);
    const [currentTrophyId, setCurrentTrophyId] = useState(null);

    const {data: trophyList, isLoading: isTrophyListLoading, error: trophyListError} = useGetPlayerTrophyInfo({userKey: playerData.userKey});

    useEffect(() => {
        if (trophyList?.length > 0 && !currentTrophyId) {
            setCurrentTrophyId(trophyList[0].trophyId);
        }
    }, [trophyList]);


    const currentTrophyData = trophyList?.find(t => t.trophyId === currentTrophyId);
    const challengeGroupIdx = currentTrophyData?.challengeGroupIdx;

    const {data: baseMissions, isLoading: isBaseMissionsLoading, error: baseMissionsError} = useGetTrophyMissionGroup(challengeGroupIdx);
    const {data: playerMissions, isLoading: isPlayerMissionsLoading, error: playerMissionsError} = useGetPlayerTrophyMissionInfo({userKey: playerData.userKey, trophyId: currentTrophyId});

    const manualMutation = useSetTrophyMissionManual();
    const finalRewardMutation = useSetTrophyFinalReward();

    // baseMissions(DB+CSV 병합값) + playerMissions(유저 진행도) 병합
    const rowData = (baseMissions || []).map(mission => {
        const pm = (playerMissions || []).find(m => m.challengeId === mission.groupSeq);
        const name = mission.name?.replace(/\{0\}/g, mission.conditionCount).replace(/\{1\}/g, mission.conditionValue) ?? mission.name;
        const subName = mission.subName?.replace(/\{0\}/g, mission.conditionCount).replace(/\{1\}/g, mission.conditionValue) ?? mission.subName;
        return {
            ...mission,
            name,
            subName,
            progress: pm?.progress ?? 0,
            isComplete: pm?.isComplete ?? 0,
            updated: pm?.updated ?? null,
        };
    });

    const columns = [
        {title: '번호', dataIndex: 'groupSeq', key: 'groupSeq', width: 60},
        {title: '미션 내용', dataIndex: 'subName', key: 'subName'},
        {
            title: '진행',
            key: 'progress',
            width: 110,
            render: (_, {progress, conditionCount}) => `${progress} / ${conditionCount}`,
        },
        {
            title: '완료',
            dataIndex: 'isComplete',
            key: 'isComplete',
            width: 80,
            render: (v) => {
                const map = {0: {text: '미완료', color: 'default'}, 1: {text: '완료', color: 'green'}};
                const info = map[v] || {text: v, color: 'default'};
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        {
            title: '보상 수령 시간',
            key: 'updated',
            width: 160,
            render: (_, {isComplete, updated}) => isComplete === 1 ? updated : '-',
        },
        {
            title: '수동처리',
            key: 'manual',
            width: 100,
            render: (_, record) => (
                <Popconfirm
                    title="해당 미션 보상을 수동으로 발송하시겠습니까?"
                    onConfirm={() => manualMutation.mutate({
                        userKey: playerData.userKey,
                        trophyId: currentTrophyId,
                        challengeId: record.groupSeq,
                        challengeGroupIdx,
                    })}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" loading={manualMutation.isPending} disabled={record.isComplete === 1}>수동처리</Button>
                </Popconfirm>
            ),
        },
    ];

    if (isTrophyListLoading) return <IsLoading/>;
    if (trophyListError) return <ErrorPage error={trophyListError}/>;

    return (
        <div>
            트로피 선택:
            <Select
                value={currentTrophyId}
                onChange={(value) => setCurrentTrophyId(value)}
                style={{minWidth: 120}}
                size="small"
            >
                {(trophyList || []).map(t => (
                    <Select.Option key={t.trophyId} value={t.trophyId}>
                        {t.trophyId}
                    </Select.Option>
                ))}
            </Select>
            <Descriptions title="트로피 정보" bordered size="small" style={{marginTop: 16, marginBottom: 16}}>
                <Descriptions.Item label="트로피 ID">{currentTrophyData?.trophyId ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="챌린지 그룹">{currentTrophyData?.challengeGroupIdx ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="최종 보상 수령">
                    <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                        <span>{currentTrophyData?.finalRewardClaim ?? '-'}</span>
                        <Popconfirm
                            title="최종 보상을 수동으로 지급하시겠습니까?"
                            onConfirm={() => finalRewardMutation.mutate({
                                userKey: playerData.userKey,
                                trophyId: currentTrophyId,
                            })}
                            okText="확인"
                            cancelText="취소"
                            disabled={!currentTrophyId || !!currentTrophyData?.finalRewardClaim}
                        >
                            <Button danger size="small" loading={finalRewardMutation.isPending} disabled={!currentTrophyId || !!currentTrophyData?.finalRewardClaim}>
                                최종보상 지급
                            </Button>
                        </Popconfirm>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="시작일">{currentTrophyData?.created ?? '-'}</Descriptions.Item>
            </Descriptions>
            {(isBaseMissionsLoading || isPlayerMissionsLoading) ? <IsLoading/> :
                (baseMissionsError || playerMissionsError) ? <ErrorPage error={baseMissionsError || playerMissionsError}/> : (
                    <Table
                        columns={columns}
                        dataSource={rowData}
                        rowKey="groupSeq"
                        pagination={false}
                        bordered
                        size="small"
                    />
                )}
        </div>
    );
};

export default PlayerTrophy;